import { borderRadius, palette, spaces } from '@kimuno/theme';
import { Box, OutlinedInput, Stack, Typography } from '@mui/material';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getTemporaryAttachments } from '../../../../../reducers/aiAssistant.selectors';
import { useId } from '../../../../../utils/useId';
import useScreenSize from '../../../../../utils/useScreenSize';
import AIUploadFileList from './AIUploadFileList';

type PromptInputProps = {
	onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
	promptValue: string;
	maxPromptLength?: number;
	placeholder?: string;
	leftItems?: React.ReactNode;
	rightItems?: React.ReactNode;
	'aria-label'?: string;
};

const PromptInput: React.FC<PromptInputProps> = ({
	onChange,
	onKeyDown,
	promptValue,
	maxPromptLength,
	placeholder,
	leftItems,
	rightItems,
	'aria-label': ariaLabel,
}) => {
	const { t } = useTranslation();
	const { isXsScreen, isSmScreen } = useScreenSize();
	const temporaryAttachments = useSelector(getTemporaryAttachments);
	const fieldId = useId('ai-prompt-input');
	const characterCountId = `${fieldId}-character-count`;

	const getMaxInputRows = () => {
		if (isXsScreen) {
			return 5;
		}
		if (isSmScreen) {
			return 10;
		}
		return 20;
	};

	const isAtMaxLength = maxPromptLength ? promptValue.length >= maxPromptLength : false;

	return (
		<Box sx={{ position: 'relative' }}>
			<Stack
				gap={spaces.xs}
				sx={{
					padding: spaces.xs,
					borderRadius: borderRadius.md,
					backgroundColor: palette.background.paperElevation2,
				}}
			>
				{temporaryAttachments.length > 0 && (
					<AIUploadFileList uploadedAttachments={temporaryAttachments} withRemoveButton />
				)}
				<OutlinedInput
					id={fieldId}
					data-testid="ai-assistant-prompt-input"
					onChange={onChange}
					onKeyDown={onKeyDown}
					value={promptValue}
					minRows={1}
					maxRows={getMaxInputRows()}
					multiline
					placeholder={placeholder}
					fullWidth
					aria-label={ariaLabel || placeholder || t('AI Assistant prompt input')}
					aria-describedby={maxPromptLength ? characterCountId : undefined}
					aria-invalid={isAtMaxLength ? true : undefined}
					sx={{
						padding: 0,
						'& .MuiOutlinedInput-notchedOutline': {
							border: 'none',
						},
					}}
					inputProps={{
						sx: {
							padding: `${spaces.xs} ${spaces.sm}`,
							borderRadius: borderRadius.sm,
						},
					}}
				/>
				<Stack direction="row" justifyContent="space-between">
					<Stack direction="row" gap={spaces.xs} alignItems="center">
						{leftItems}
					</Stack>
					<Stack direction="row" gap={spaces.xs} alignItems="center">
						{rightItems}
					</Stack>
				</Stack>
			</Stack>
			{maxPromptLength && (
				<Box sx={{ paddingLeft: spaces.md, paddingTop: spaces.xxs }}>
					<Typography
						id={characterCountId}
						data-testid="ai-assistant-user-prompt-length"
						variant="caption"
						color={isAtMaxLength ? palette.error.main : palette.text.secondary}
						role="status"
						aria-live="polite"
						aria-atomic="true"
					>
						{promptValue.length}/{maxPromptLength} {t('characters')}
					</Typography>
				</Box>
			)}
		</Box>
	);
};

export default PromptInput;
